/**
 * Life4Earth V2 — Project Roles API Adapter
 *
 * Three-tier role model:
 *   Tier 1: Role Definitions (admin catalog) — /api/admin/role-definitions, /api/role-definitions
 *   Tier 2: Project Role Slots — /api/projects/{guid}/roles
 *   Tier 3: Assignments — /api/projects/{guid}/roles/{slotGuid}/assign
 *
 * Also: category defaults, platform skills, community open roles.
 */

import { apiFetch } from "./client";

// ════════════════════════════════════════════════════════════════
// Response Types (match ASP.NET JSON serialization — camelCase)
// ════════════════════════════════════════════════════════════════

// ── Role Definitions (Tier 1) ──

export interface RoleDefinitionResponse {
  definitionGuid: string;
  roleCategory: string;
  label: string;
  description: string | null;
  dimensionWeights: string; // JSONB string
  skillsSuggested: string[];
  timeCommitmentHint: string | null;
  concernCategories: string[];
  isActive: boolean;
  promotedFromGuid: string | null;
  usageCount: number;
  sortOrder: number;
  translations: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** DEV-1157 Phase 2d. Tier-1 → Role Rhythm linkage. When non-null,
   *  every slot copied from this definition (via source_definition_guid)
   *  inherits the rhythm — instances materialize on user link. */
  roleRhythmGuid: string | null;
}

// ── Project Role Slots (Tier 2) ──

export interface ProjectRoleSlotResponse {
  roleSlotGuid: string;
  projectGuid: string;
  sourceDefinitionGuid: string | null;
  sourceWorkspaceGuid: string | null;
  sourceTemplateGuid: string | null;
  roleCategory: string;
  roleLabel: string;
  description: string | null;
  phase: string;
  slotsNeeded: number;
  slotsFilled: number;
  status: string;
  visibility: string;
  isOwnerDefault: boolean;
  dimensionWeights: string | null; // JSONB string or null
  verificationLevel: string; // auto, quick_confirm, standard, evidence_required
  skillsRequired: string[];
  timeCommitment: string | null;
  durationType: string; // project_duration, phase_duration, fixed_term, ongoing
  expectedStartDate: string | null;
  expectedEndDate: string | null;
  sortOrder: number;
  /** Which role slot this slot reports to (org chart hierarchy). NULL = root node. */
  reportsToSlotGuid: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  assignments: ProjectRoleAssignmentResponse[] | null;
}

// ── Assignments (Tier 3) ──

export interface ProjectRoleAssignmentResponse {
  assignmentGuid: string;
  roleSlotGuid: string;
  userGuid: string;
  status: string;
  assignedAt: string;
  completedAt: string | null;
  assignedBy: string | null;
  removedAt: string | null;
  removedBy: string | null;
  impactAccrued: boolean;
  notes: string | null;
  instructions: string | null;
  evidenceUrls: string[];
  evidenceNotes: string | null;
  /** Team member visibility: public, team_only, anonymous. */
  visibility: string;
  // Duration
  expectedStartDate: string | null;
  expectedEndDate: string | null;
  // Invitation tracking
  invitedAt: string | null;
  invitationViewedAt: string | null;
  invitationRespondedAt: string | null;
  invitationMessage: string | null;
  // Resignation
  resignedAt: string | null;
  resignationReason: string | null;
}

// ── Summary ──

export interface CategorySummary {
  total: number;
  filled: number;
  peopleNeeded: number;
  peopleFilled: number;
}

export interface ProjectRoleSlotSummary {
  totalSlots: number;
  filledSlots: number;
  openSlots: number;
  draftSlots: number;
  totalPeopleNeeded: number;
  totalPeopleFilled: number;
  byCategory: Record<string, CategorySummary>;
}

// ── Category Defaults ──

export interface RoleCategoryDefaultResponse {
  roleCategory: string;
  dimensionWeights: string; // JSONB string
  description: string | null;
}

// ── Platform Skills ──

export interface PlatformSkillResponse {
  id: number;
  slug: string;
  label: string;
  domain: string;
  parentSlug: string | null;
  usageCount: number;
  isCanonical: boolean;
}

// ════════════════════════════════════════════════════════════════
// Request Types
// ════════════════════════════════════════════════════════════════

export interface UpsertRoleDefinitionRequest {
  roleCategory: string;
  label: string;
  description?: string | null;
  dimensionWeights: string;
  skillsSuggested?: string[];
  timeCommitmentHint?: string | null;
  concernCategories?: string[];
  isActive?: boolean;
  sortOrder?: number;
  translations?: string | null;
}

export interface CreateProjectRoleSlotRequest {
  sourceDefinitionGuid?: string | null;
  roleCategory: string;
  roleLabel: string;
  description?: string | null;
  phase?: string;
  slotsNeeded?: number;
  status?: string;
  visibility?: string;
  isOwnerDefault?: boolean;
  dimensionWeights?: string | null;
  skillsRequired?: string[];
  timeCommitment?: string | null;
  durationType?: string; // project_duration, phase_duration, fixed_term, ongoing
  expectedStartDate?: string | null;
  expectedEndDate?: string | null;
  sortOrder?: number;
  /** Org chart hierarchy: which slot this reports to. NULL = root. */
  reportsToSlotGuid?: string | null;
}

export interface UpdateProjectRoleSlotRequest {
  roleCategory?: string;
  roleLabel?: string;
  description?: string;
  phase?: string;
  slotsNeeded?: number;
  status?: string;
  visibility?: string;
  isOwnerDefault?: boolean;
  dimensionWeights?: string;
  skillsRequired?: string[];
  timeCommitment?: string;
  durationType?: string;
  expectedStartDate?: string;
  expectedEndDate?: string;
  sortOrder?: number;
  /** Change reporting line. Set to another slot's GUID or null for root. */
  reportsToSlotGuid?: string | null;
  /** Explicitly clear reports_to (make root node). */
  clearReportsTo?: boolean;
}

// ── Team Structure (batch hierarchy update) ──

export interface SlotHierarchyEntry {
  roleSlotGuid: string;
  reportsToSlotGuid: string | null;
  sortOrder?: number;
}

export interface UpdateTeamStructureRequest {
  entries: SlotHierarchyEntry[];
}

export interface BulkCreateRoleSlotsRequest {
  slots: CreateProjectRoleSlotRequest[];
  sourceTemplateGuid?: string | null;
  sourceWorkspaceGuid?: string | null;
}

export interface CreateRoleAssignmentRequest {
  userGuid: string;
  /** Set to the team lead's GUID for direct assignment. NULL = volunteer self-application. */
  assignedBy?: string | null;
  notes?: string | null;
  /** If true, create as pending_invitation instead of direct assignment. */
  isInvitation?: boolean;
  /** Optional message from PM accompanying the invitation. */
  invitationMessage?: string | null;
  /** Override expected start date (NULL = inherit from slot). */
  expectedStartDate?: string | null;
  /** Override expected end date (NULL = inherit from slot). */
  expectedEndDate?: string | null;
}

export interface UpdateRoleAssignmentRequest {
  status?: string;
  notes?: string;
  instructions?: string;
  evidenceUrls?: string[];
  evidenceNotes?: string;
  /** Team member visibility: public, team_only, anonymous. */
  visibility?: string;
  /** Reason for resignation (when status = resigned). */
  resignationReason?: string;
}

// ════════════════════════════════════════════════════════════════
// API Functions — Role Definitions (Tier 1)
// ════════════════════════════════════════════════════════════════

/** List all catalog definitions (admin). */
export async function fetchRoleDefinitionsAdmin(
  isActive?: boolean
): Promise<RoleDefinitionResponse[]> {
  const params = isActive !== undefined ? `?isActive=${isActive}` : "";
  return apiFetch<RoleDefinitionResponse[]>(`/api/admin/role-definitions${params}`);
}

/** List active catalog definitions (public, optionally filtered by concern category). */
export async function fetchRoleDefinitions(
  concernCategory?: string
): Promise<RoleDefinitionResponse[]> {
  const params = concernCategory ? `?concernCategory=${encodeURIComponent(concernCategory)}` : "";
  return apiFetch<RoleDefinitionResponse[]>(`/api/role-definitions${params}`);
}

/** Get a single role definition by GUID. */
export async function fetchRoleDefinition(
  guid: string
): Promise<RoleDefinitionResponse> {
  return apiFetch<RoleDefinitionResponse>(`/api/admin/role-definitions/${guid}`);
}

/** Create a new role definition in the catalog. */
export async function createRoleDefinition(
  req: UpsertRoleDefinitionRequest
): Promise<RoleDefinitionResponse> {
  return apiFetch<RoleDefinitionResponse>("/api/admin/role-definitions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
}

/** Update an existing role definition. */
export async function updateRoleDefinition(
  guid: string,
  req: UpsertRoleDefinitionRequest
): Promise<RoleDefinitionResponse> {
  return apiFetch<RoleDefinitionResponse>(`/api/admin/role-definitions/${guid}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
}

/** Promote a project role slot to the catalog. */
export async function promoteRoleToCatalog(
  slotGuid: string
): Promise<RoleDefinitionResponse> {
  return apiFetch<RoleDefinitionResponse>(`/api/admin/role-definitions/promote/${slotGuid}`, {
    method: "POST",
  });
}

/** List promotion candidates (custom roles used on 2+ projects). */
export async function fetchPromotionCandidates(): Promise<ProjectRoleSlotResponse[]> {
  return apiFetch<ProjectRoleSlotResponse[]>("/api/admin/role-definitions/candidates");
}

// ════════════════════════════════════════════════════════════════
// API Functions — Category Defaults
// ════════════════════════════════════════════════════════════════

/** List all category default dimension weights. */
export async function fetchCategoryDefaults(): Promise<RoleCategoryDefaultResponse[]> {
  return apiFetch<RoleCategoryDefaultResponse[]>("/api/admin/role-category-defaults");
}

/** Update dimension weights for a specific category. */
export async function updateCategoryDefault(
  category: string,
  dimensionWeights: string,
  description?: string
): Promise<RoleCategoryDefaultResponse> {
  return apiFetch<RoleCategoryDefaultResponse>(
    `/api/admin/role-category-defaults/${encodeURIComponent(category)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dimensionWeights, description }),
    }
  );
}

// ════════════════════════════════════════════════════════════════
// API Functions — Platform Skills
// ════════════════════════════════════════════════════════════════

/** List skills for autocomplete. */
export async function fetchPlatformSkills(opts?: {
  domain?: string;
  search?: string;
  canonicalOnly?: boolean;
}): Promise<PlatformSkillResponse[]> {
  const params = new URLSearchParams();
  if (opts?.domain) params.set("domain", opts.domain);
  if (opts?.search) params.set("search", opts.search);
  if (opts?.canonicalOnly) params.set("canonicalOnly", "true");
  const qs = params.toString();
  return apiFetch<PlatformSkillResponse[]>(`/api/platform-skills${qs ? `?${qs}` : ""}`);
}

// ════════════════════════════════════════════════════════════════
// API Functions — Project Role Slots (Tier 2)
// ════════════════════════════════════════════════════════════════

/** List all role slots for a project (includes active assignments). */
export async function fetchProjectRoleSlots(
  projectGuid: string
): Promise<ProjectRoleSlotResponse[]> {
  return apiFetch<ProjectRoleSlotResponse[]>(
    `/api/projects/${projectGuid}/roles`
  );
}

/** Create a new role slot on a project. */
export async function createProjectRoleSlot(
  projectGuid: string,
  req: CreateProjectRoleSlotRequest
): Promise<ProjectRoleSlotResponse> {
  return apiFetch<ProjectRoleSlotResponse>(
    `/api/projects/${projectGuid}/roles`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/** Update an existing role slot (partial update). */
export async function updateProjectRoleSlot(
  projectGuid: string,
  slotGuid: string,
  req: UpdateProjectRoleSlotRequest
): Promise<ProjectRoleSlotResponse> {
  return apiFetch<ProjectRoleSlotResponse>(
    `/api/projects/${projectGuid}/roles/${slotGuid}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/** Close (soft-delete) a role slot. Throws with `error: 'ACTIVE_ASSIGNMENTS'` if active assignments exist. */
export async function closeProjectRoleSlot(
  projectGuid: string,
  slotGuid: string
): Promise<void> {
  return apiFetch<void>(
    `/api/projects/${projectGuid}/roles/${slotGuid}`,
    { method: "DELETE" }
  );
}

/** Reopen a previously closed role slot. */
export async function reopenProjectRoleSlot(
  projectGuid: string,
  slotGuid: string
): Promise<ProjectRoleSlotResponse> {
  return apiFetch<ProjectRoleSlotResponse>(
    `/api/projects/${projectGuid}/roles/${slotGuid}/reopen`,
    { method: "POST" }
  );
}

/** Get aggregate summary of role slots for a project. */
export async function fetchProjectRoleSlotSummary(
  projectGuid: string
): Promise<ProjectRoleSlotSummary> {
  return apiFetch<ProjectRoleSlotSummary>(
    `/api/projects/${projectGuid}/roles/summary`
  );
}

/** Bulk create role slots from a template or workspace plan. */
export async function bulkCreateProjectRoleSlots(
  projectGuid: string,
  req: BulkCreateRoleSlotsRequest
): Promise<ProjectRoleSlotResponse[]> {
  return apiFetch<ProjectRoleSlotResponse[]>(
    `/api/projects/${projectGuid}/roles/bulk`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/** Batch update sort_order for role slots (reordering). */
export async function reorderProjectRoleSlots(
  projectGuid: string,
  slotOrders: Array<{ roleSlotGuid: string; sortOrder: number }>
): Promise<void> {
  // Use individual updates — no dedicated batch endpoint yet
  await Promise.all(
    slotOrders.map(({ roleSlotGuid, sortOrder }) =>
      apiFetch<ProjectRoleSlotResponse>(
        `/api/projects/${projectGuid}/roles/${roleSlotGuid}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sortOrder }),
        }
      )
    )
  );
}

// ════════════════════════════════════════════════════════════════
// API Functions — Assignments (Tier 3)
// ════════════════════════════════════════════════════════════════

/** Assign a user to a role slot. */
export async function assignUserToRole(
  projectGuid: string,
  slotGuid: string,
  req: CreateRoleAssignmentRequest
): Promise<ProjectRoleAssignmentResponse> {
  return apiFetch<ProjectRoleAssignmentResponse>(
    `/api/projects/${projectGuid}/roles/${slotGuid}/assign`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/** Update an assignment (status, notes). */
export async function updateRoleAssignment(
  projectGuid: string,
  slotGuid: string,
  assignGuid: string,
  req: UpdateRoleAssignmentRequest
): Promise<ProjectRoleAssignmentResponse> {
  return apiFetch<ProjectRoleAssignmentResponse>(
    `/api/projects/${projectGuid}/roles/${slotGuid}/assign/${assignGuid}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/** Remove/withdraw an assignment. */
export async function removeRoleAssignment(
  projectGuid: string,
  slotGuid: string,
  assignGuid: string
): Promise<void> {
  return apiFetch<void>(
    `/api/projects/${projectGuid}/roles/${slotGuid}/assign/${assignGuid}`,
    { method: "DELETE" }
  );
}

// ════════════════════════════════════════════════════════════════
// API Functions — Team Structure (Hierarchy)
// ════════════════════════════════════════════════════════════════

/** Batch update reporting hierarchy (draft/commit pattern). */
export async function updateTeamStructure(
  projectGuid: string,
  req: UpdateTeamStructureRequest
): Promise<ProjectRoleSlotResponse[]> {
  return apiFetch<ProjectRoleSlotResponse[]>(
    `/api/projects/${projectGuid}/roles/structure`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

// ════════════════════════════════════════════════════════════════
// API Functions — Community Open Roles
// ════════════════════════════════════════════════════════════════

/** List public open roles for a community (cross-project). */
export async function fetchCommunityOpenRoles(
  communityGuid: string
): Promise<ProjectRoleSlotResponse[]> {
  return apiFetch<ProjectRoleSlotResponse[]>(
    `/api/communities/${communityGuid}/open-roles`
  );
}
